/**
 * 运营策略列表页自定义Hook
 * 处理SKU列表的加载、创建和删除
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormInstance, App } from 'antd';
import axios from 'axios';

export interface SKUStrategyItem {
    id: number;
    name: string;
    asin: string;
    sku_code: string;
    price: number | null;
    profit_margin: number | null;
    cost_price: number | null;
    purchase_cost_cny: number | null;
    status: string;
    seller_mappings?: Array<{
        id: number;
        sku_code: string;
        shop_name: string;
        asin: string;
        title: string;
    }>;
}

interface UseOperationStrategyListProps {
    createForm: FormInstance;
}

interface UseOperationStrategyListReturn {
    loading: boolean;
    skuList: SKUStrategyItem[];
    createModalVisible: boolean;
    setCreateModalVisible: (visible: boolean) => void;
    handleViewStrategy: (record: SKUStrategyItem) => void;
    handleCreateSKU: () => Promise<void>;
    handleDeleteSKU: (record: SKUStrategyItem) => Promise<void>;
    // Edit related
    editModalVisible: boolean;
    setEditModalVisible: (visible: boolean) => void;
    currentSKU: SKUStrategyItem | null;
    handleEditSKU: (record: SKUStrategyItem) => void;
    handleUpdateSKU: () => Promise<void>;
}

export const useOperationStrategyList = ({
    createForm,
}: UseOperationStrategyListProps): UseOperationStrategyListReturn => {
    const navigate = useNavigate();
    const { message } = App.useApp();

    const [loading, setLoading] = useState(false);
    const [skuList, setSKUList] = useState<SKUStrategyItem[]>([]);
    const [createModalVisible, setCreateModalVisible] = useState(false);

    // Edit State
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [currentSKU, setCurrentSKU] = useState<SKUStrategyItem | null>(null);

    // 加载SKU列表
    const loadSKUList = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/skus/');
            const data = response.data;

            if (Array.isArray(data)) {
                setSKUList(data);
            } else if (data && typeof data === 'object' && 'results' in data) {
                setSKUList((data as any).results || []);
            } else {
                setSKUList([]);
            }
        } catch (error) {
            console.error('加载SKU列表失败:', error);
            setSKUList([]);
        } finally {
            setLoading(false);
        }
    }, []);

    // 查看策略详情
    const handleViewStrategy = useCallback((record: SKUStrategyItem) => {
        navigate(`/operation-strategy/${record.id}`);
    }, [navigate]);

    // 创建SKU
    const handleCreateSKU = useCallback(async () => {
        try {
            const values = await createForm.validateFields();
            setLoading(true);

            const response = await axios.post('/api/skus/', {
                name: values.name,
                asin: values.asin || '',
                sku_code: values.sku_code || '',
                status: 'planning',
            });

            message.success('SKU创建成功');
            setCreateModalVisible(false);
            createForm.resetFields();
            loadSKUList();

            // 创建后直接跳转到详情页（使用新SKU的ID）
            const newSkuId = response.data?.id;
            if (newSkuId) {
                navigate(`/operation-strategy/${newSkuId}`);
            }
        } catch (error: any) {
            console.error('创建SKU失败:', error);
            if (error.response?.data?.name) {
                message.error('SKU名称已存在');
            } else {
                message.error('创建失败，请重试');
            }
        } finally {
            setLoading(false);
        }
    }, [createForm, message, navigate, loadSKUList]);

    // 删除SKU
    const handleDeleteSKU = useCallback(async (record: SKUStrategyItem) => {
        try {
            setLoading(true);
            await axios.delete(`/api/skus/${record.id}/`);
            message.success('删除成功');
            loadSKUList();
        } catch (error) {
            console.error('删除失败:', error);
            message.error('删除失败');
        } finally {
            setLoading(false);
        }
    }, [message, loadSKUList]);

    // 打开编辑弹窗
    const handleEditSKU = useCallback((record: SKUStrategyItem) => {
        setCurrentSKU(record);
        createForm.setFieldsValue({
            name: record.name,
            asin: record.asin,
            sku_code: record.sku_code,
            status: record.status,
        });
        setEditModalVisible(true);
    }, [createForm]);

    // 更新SKU
    const handleUpdateSKU = useCallback(async () => {
        if (!currentSKU) return;
        try {
            const values = await createForm.validateFields();
            setLoading(true);

            await axios.patch(`/api/skus/${currentSKU.id}/`, {
                name: values.name,
                asin: values.asin,
                sku_code: values.sku_code,
                status: values.status,
            });

            message.success('更新成功');
            setEditModalVisible(false);
            setCurrentSKU(null);
            createForm.resetFields();
            loadSKUList();
        } catch (error) {
            console.error('更新失败:', error);
            message.error('更新失败');
        } finally {
            setLoading(false);
        }
    }, [currentSKU, createForm, message, loadSKUList]);

    // 初始化加载
    useEffect(() => {
        loadSKUList();
    }, [loadSKUList]);

    return {
        loading,
        skuList,
        createModalVisible,
        setCreateModalVisible,
        handleViewStrategy,
        handleCreateSKU,
        handleDeleteSKU,
        // Edit
        editModalVisible,
        setEditModalVisible,
        currentSKU,
        handleEditSKU,
        handleUpdateSKU,
    };
};

export default useOperationStrategyList;
